import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FaLaptopCode, FaPalette, FaUser, FaBriefcase } from 'react-icons/fa';
import { Card, CardActionArea, CardMedia, CardContent, Typography, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [showTitle, setShowTitle] = useState(null);
  const toggleDarkMode = () => setDarkMode(!darkMode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const projects = [
    {
      title: "Connecto",
      image: "./Connecto Logo.png",
      color: "connecto-color",
      path: "/projects"
    },
    {
      title: "Pro Resume Crafters",
      image: "./Pro.png",
      color: "pro-crafters-color",
      path: "/projects"
    },
    {
      title: "Kelvin Portfolio",
      image: "./portfolio.PNG",
      color: "kelvin-portfolio-color",
      path: "/projects"
    },
    {
      title: "Ian Portfolio",
      image: "./ian.jpeg",
      color: "ian-portfolio-color",
      path: "/projects"
    },
    {
      title: "Kelvin Online CV",
      image: "/cv.png",
      color: "kelvin-cv-color",
      path: "/projects"
    },
    {
      title: "Alex Online CV",
      image: "/Alex.png",
      color: "alex-cv-color",
      path: "/projects"
    },
  ];

  const handleProjectClick = (path) => {
    navigate(path);
  };

  const services = [
    {
      id: 1,
      icon: <FaLaptopCode size={80} className="icon-animation" />,
      title: "Full Stack Website Development",
      features: [
        "Front-end and back-end integration",
        "Robust & scalable websites",
        "Sleek design and seamless deployment",
        "Duration: 3-4 weeks"
      ],
    },
    {
      id: 2,
      icon: <FaPalette size={80} className="icon-animation" />,
      title: "Static Website Design",
      features: [
        "Creative & efficient design",
        "Powerful online identity",
        "Ideal for portfolios and businesses",
        "Duration: 2-3 weeks"
      ],
    },
    {
      id: 3,
      icon: <FaUser size={80} className="icon-animation" />,
      title: "Online CV Creation",
      features: [
        "Professional & modern CVs",
        "Highlight skills digitally",
        "Engaging presentation",
        "Ready in just 24 hours"
      ],
    },
    {
      id: 4,
      icon: <FaBriefcase size={80} className="icon-animation" />,
      title: "Portfolio Development",
      features: [
        "Personalized & professional portfolios",
        "Reflects talent and accomplishments",
        "Visually stunning designs",
        "Ready in just 48 hours"
      ],
    },
  ];

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    cssEase: "linear",
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const cardVariants = {
    offscreen: { opacity: 0, y: 100 },
    onscreen: { opacity: 1, y: 0, transition: { type: "spring", bounce: 0.4, duration: 0.8 } }
  };

  const cardMotionVariants = {
    initial: { scale: 0.95, opacity: 0 },
    enter: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
    whileHover: { scale: 1.05 }
  };

  useEffect(() => {
    const adjustCardSize = () => {
      const projectContainers = document.querySelectorAll('.project-card-container');
      if (projectContainers.length) {
        const width = projectContainers[0].offsetWidth;
        projectContainers.forEach(container => {
          container.style.height = `${width}px`;
          container.style.width = `${width}px`;
        });
      }
    };

    window.addEventListener('resize', adjustCardSize);
    adjustCardSize();

    return () => window.removeEventListener('resize', adjustCardSize);
  }, []);

  return (
    <div className={`landing-page ${darkMode ? 'dark-mode' : ''}`}>
      <IconButton onClick={toggleDarkMode} className="toggle-dark-mode">
        {darkMode ? <Brightness7 /> : <Brightness4 />}
      </IconButton>

      <div className="hero-section">
        <video
          className="video-background"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="./craftt.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      <Container className="landing-service-container">
        <Row className="justify-content-center mb-5">
          <h2 className="landing-service-title text-center">What We Offer</h2>
        </Row>
        <Row className="justify-content-center text-center">
          {services.map((service, index) => (
            <Col xs={12} sm={6} md={4} lg={3} key={service.id} className="mb-4 d-flex align-items-stretch">
              <motion.div
                variants={cardMotionVariants}
                initial="initial"
                animate="enter"
                whileHover="whileHover"
                className="landing-service-card"
              >
                {React.cloneElement(service.icon, { size: 80, className: "icon-animation" })} 
                <h3>{service.title}</h3>
                <ul className="custom-bullet-list">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ul>
                <Button variant="contained" color="primary" component={Link} to="/services">
                  Learn More
                </Button>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="project-container">
        <Row className="justify-content-center mb-5">
          <h2 className="project-title text-center">Featured Projects</h2>
        </Row>
        <Slider {...settings}>
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              initial="offscreen"
              animate="onscreen"
              className="project-card-container"
              onMouseEnter={() => setShowTitle(index)}
              onMouseLeave={() => setShowTitle(null)}
              style={{ margin: '10px' }}
            >
              <Card
                className={`project-card ${showTitle === index ? 'hover' : ''}`}
                onClick={() => handleProjectClick(project.path)}
              >
                <CardActionArea>
                  <CardMedia
                    component="img"
                    alt={project.title}
                    image={project.image}
                    className="project-card-image"
                    style={{ height: '100%', width: '100%' }}
                  />
                  {showTitle === index && (
                    <CardContent className="card-content">
                      <Typography gutterBottom variant="h5" component="div" className="project-card-title">
                        {project.title}
                      </Typography>
                    </CardContent>
                  )}
                </CardActionArea>
              </Card>
            </motion.div>
          ))}
        </Slider>
      </Container>
    </div>
  );
};

export default LandingPage;